import React, { useState, Fragment, useRef, useEffect } from 'react';
import { QrReader } from 'react-qr-reader';
import {
    Button,
    Card,
    Typography,
    Space,
    message,
    Input,
    Tabs,
} from 'antd';
import {
    QrcodeOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import './QRScan.less';
import Header from 'commons/components/Header/Header';
import ModalConfirmSendMail from 'commons/components/Modal/ModalConfirmSendMail';
import services from "commons/apis/index";
import { useTranslation } from 'react-i18next';

const { Title, Paragraph, Text } = Typography;

const QRScan = () => {
    const { t } = useTranslation();
    const [isScanning, setIsScanning] = useState(false);
    const [scannedData, setScannedData] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('scan');
    const [bookingInput, setBookingInput] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const inputRef = useRef(null);

    const handleScan = async (result, error) => {
        if (result) {
            const response = await services.wonPrizeBookingForAdminByCustomId({ bookingCustomId: result?.text?.trim() });
            if (response.success) {
                setScannedData(response.result);
                setIsScanning(false);
                setError('');
                message.success(t('qr-scan.qr-success'));
            } else {
                setIsScanning(false);
                setError(response?.error?.message);
                message.error(response?.error?.message);
            }
        }

        if (error) {
            if (error.name !== 'NotFoundException') {
                console.warn('QR Scan Error:', error);
            }
        }
    };

    const handleManualSearch = async () => {
        if (!bookingInput.trim()) {
            message.warning(t('qr-scan.empty-booking-warning'));
            return;
        }

        setIsSearching(true);
        setError('');
        const response = await services.wonPrizeBookingForAdminByCustomId({ bookingCustomId: bookingInput.trim() });
        if (response.success) {
            setScannedData(response.result);
            message.success(t('qr-scan.search-success'));
        } else {
            setError(response?.error?.message);
            message.error(response?.error?.message);
        }
        setIsSearching(false);
    };

    const startScanning = async () => {
        try {
            setError('');
            setScannedData('');
            setIsScanning(true);
        } catch (err) {
            setError(t('qr-scan.camera-error'));
            setIsScanning(false);
            message.error(t('qr-scan.qr-error'));
        }
    };

    const stopScanning = () => {
        setIsScanning(false);
    };

    const clearResult = () => {
        setScannedData('');
        setError('');
        setBookingInput('');
        if (activeTab === 'manual' && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    };

    const handleConfirm = async () => {
        setIsSending(true);
        const response = await services.sendMailToCustomerSelection({ bookingId: scannedData?.id });
        if (response?.success) {
            message.success(t('modal.sendmail.success', { cusName: scannedData?.customerName }));
            setIsModalVisible(false);
            clearResult();
            if (activeTab === 'manual' && inputRef.current) {
                setTimeout(() => {
                    inputRef.current?.focus();
                }, 100);
            }
        } else {
            message.error(t('modal.sendmail.error', { cusName: scannedData?.customerName }));
        }
        setIsSending(false);
    };

    useEffect(() => {
        setTimeout(() => {
            if (activeTab === 'manual' && inputRef.current) {
                inputRef.current?.focus();
            }
        }, 100);
    }, [activeTab]);


    const tabItems = [
        {
            key: 'scan',
            label: (
                <span>
                    <QrcodeOutlined /> {t('qr-scan.title')}
                </span>
            ),
            children: (
                <div className="tab-content">
                    <div className="camera-container">
                        <div className="camera-view">
                            {isScanning ? (
                                <QrReader
                                    onResult={handleScan}
                                    constraints={{ facingMode: "environment" }}
                                    className="qr-reader"
                                    containerStyle={{ width: '100%', height: '250px' }}
                                    videoStyle={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                    }}
                                />
                            ) : (
                                <div className="camera-placeholder">
                                    <QrcodeOutlined className="camera-icon" />
                                </div>
                            )}
                            {isScanning && (
                                <div className="scanning-overlay">
                                    <div className="scanning-frame">
                                        <div className="corner corner-tl"></div>
                                        <div className="corner corner-tr"></div>
                                        <div className="corner corner-bl"></div>
                                        <div className="corner corner-br"></div>
                                        <div className="scanning-line"></div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="controls-container">
                        {!isScanning ? (
                            <Button
                                type="primary"
                                onClick={startScanning}
                                className="start-button"
                                block>
                                {t('qr-scan.start-scan')}
                            </Button>
                        ) : (
                            <Button
                                danger
                                onClick={stopScanning}
                                className="stop-button"
                                block>
                                {t('qr-scan.stop-scan')}
                            </Button>
                        )}
                    </div>
                </div>
            ),
        },
        {
            key: 'manual',
            label: (
                <span>
                    <SearchOutlined /> {t('qr-scan.search')}
                </span>
            ),
            children: (
                <div className="tab-content">
                    <div className="manual-input-container">
                        <Input
                            placeholder={t('qr-scan.input-placeholder')}
                            value={bookingInput}
                            onChange={(e) => setBookingInput(e.target.value)}
                            onPressEnter={handleManualSearch}
                            className="booking-input"
                            size="large"
                            ref={inputRef}
                        />
                        <Button
                            type="primary"
                            size="large"
                            onClick={handleManualSearch}
                            loading={isSearching}
                            className="search-button"
                            block
                        >
                            {t('qr-scan.search')}
                        </Button>
                    </div>
                </div>
            ),
        }
    ];

    return (
        <Fragment>
            <div className="container-fix">
                <div className="body-container">
                    <Header />
                    <div className="qr-scanner-content">
                        <div className="qr-scanner-header">
                            <div className="qr-scanner-icon">
                                <QrcodeOutlined />
                            </div>
                            <Title level={2} className="qr-scanner-title">{t('qr-scan.title')}</Title>
                            <Paragraph className="qr-scanner-subtitle">
                                {t('qr-scan.description')}
                            </Paragraph>
                        </div>

                        <Card className="qr-scanner-card">
                            <Tabs
                                activeKey={activeTab}
                                onChange={setActiveTab}
                                items={tabItems}
                                className="qr-scanner-tabs"
                                centered
                            />

                            {scannedData && (
                                <Card className="result-card success-result">
                                    <Space align="start">
                                        <div className="result-content">
                                            <div className="result-title-container">
                                                <CheckCircleOutlined className="result-icon success-icon" />
                                                <Title level={5} className="result-title">{t('qr-scan.customer-info')}</Title>
                                            </div>

                                            <div className="result-data">
                                                <div><Text>{t('qr-scan.booking-id')}: {scannedData?.bookingCustomId}</Text></div>
                                                <div><Text>{t('qr-scan.order')}: {scannedData?.orderNumber}</Text></div>
                                                {/* <div><Text>{t('qr-scan.name')}: {scannedData?.customerName}</Text></div>
                                                <div><Text>{t('qr-scan.phone')}: {scannedData?.customerPhoneNumber}</Text></div> 
                                                <div><Text>{t('qr-scan.email')}: {scannedData?.customerEmail}</Text></div> */}
                                            </div>
                                            <Space className="result-actions">
                                                <Button
                                                    type="primary"
                                                    size="small"
                                                    onClick={handleConfirm}
                                                    className="send-button"
                                                    disabled={isSending}
                                                    loading={isSending}
                                                >
                                                    {t('qr-scan.send-option')}
                                                </Button>
                                                <Button
                                                    size="small"
                                                    onClick={clearResult}
                                                    className="clear-button"
                                                >
                                                    {t('qr-scan.clear')}
                                                </Button>
                                            </Space>
                                        </div>
                                    </Space>
                                </Card>
                            )}

                            {error && (
                                <Card className="result-card error-result">
                                    <Space align="start">
                                        <ExclamationCircleOutlined className="result-icon error-icon" />
                                        <div className="result-content">
                                            <Title level={5} className="result-title">{t('qr-scan.error-title')}</Title>
                                            <Text className="error-text">{error}</Text>
                                        </div>
                                    </Space>
                                </Card>
                            )}
                        </Card>

                        <div className="instructions">
                            <Paragraph className="instructions-text">
                                {t('qr-scan.instruction')}
                            </Paragraph>
                        </div>
                    </div>
                </div>
            </div>
            <ModalConfirmSendMail
                visible={isModalVisible}
                onConfirm={handleConfirm}
                onCancel={() => setIsModalVisible(false)}
                cusName={scannedData?.customerName}
                confirmLoading={isSending}
            />
        </Fragment>
    );
};

export default QRScan;